import React, { useCallback, useMemo, useState } from "react";
import { View } from "react-native";
import { StackScreenProps } from "@react-navigation/stack";
import { useNavigation } from "@react-navigation/native";
import { useAtomValue } from "jotai";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { editThirteenthSalaryCallback } from "../../atoms/core";
import ScreenCard from "../ScreenCard";
import { useI18n } from "../../hooks/i18n-hooks";
import { useTheme } from "../../hooks/use-theme";
import { useIsScreenSizeMin } from "../../hooks/useIsScreenSizeMin";
import ModalDialogFrame from "../../components/ModalDialogFrame";
import { EarningType } from "../../__generated__/graphql";
import { getIsPay13thSalaryWithSalary } from "../../utils";
import ButtonField from "../../components/ButtonField";
import Icon from "../../components/Icon";
import Switch from "../../ui_atoms/Switch";
import Gap from "../../ui_atoms/Gap";
import { BodySmallText, TitleMediumText } from "../../components/FormattedText";
import { BubbleTalk } from "../../components/BubbleTalk";
import DocuIllSvg from "../../assets/images/docu_ill.svg";
import VStack from "../../ui_atoms/VStack";

type ThirteenthSalaryEditorNavScreenProps = StackScreenProps<
    RootStackParamList,
    "ThirteenthSalaryEditorNavScreen"
>;

const ThirteenthSalaryEditorNavScreen: React.FC<ThirteenthSalaryEditorNavScreenProps> = ({
    route,
}) => {
    const {
        has13thSalary: initialHas13thSalary,
        pay13thSalaryWithSalary: initialPay13thSalaryWithSalary,
        isMandatory,
        earningType,
    } = route.params;
    const i18n = useI18n();
    const { colors, gridSize } = useTheme();
    const isLarge = useIsScreenSizeMin("large");
    const navigation = useNavigation();
    const insets = useSafeAreaInsets();
    const callback = useAtomValue(editThirteenthSalaryCallback);

    // A mandatory 13th (CCT) is always on; the 12x/13x choice stays open.
    const [has13thSalary, setHas13thSalary] = useState(isMandatory || initialHas13thSalary);
    const [pay13thSalaryWithSalary, setPay13thSalaryWithSalary] = useState(
        initialPay13thSalaryWithSalary,
    );

    const isMonthly = earningType === EarningType.Monthly;

    // Switching the 13th off resets the choice to 12x, so switching it back on starts from 12x.
    const toggleHas13thSalary = useCallback(() => {
        if (has13thSalary) {
            setPay13thSalaryWithSalary(true);
        }
        setHas13thSalary(!has13thSalary);
    }, [has13thSalary]);
    const result = useMemo(
        () => ({
            has13thSalary,
            // Resolved like the backend: without a 13th, or off monthly, it is always 12x.
            pay13thSalaryWithSalary: getIsPay13thSalaryWithSalary({
                earningType,
                has13thSalary,
                pay13thSalaryWithSalary,
            }),
        }),
        [earningType, has13thSalary, pay13thSalaryWithSalary],
    );

    const onClose = useCallback(() => {
        if (callback.func) {
            void callback.func(result);
        } else {
            navigation.goBack();
        }
    }, [callback, result, navigation]);

    const renderRegime = (is12x: boolean) => {
        const regime = is12x ? "12x" : "13x";
        const isSelected = result.pay13thSalaryWithSalary === is12x;
        return (
            <ButtonField
                testID={`thirteenth-salary-${regime}`}
                style={{ marginHorizontal: -gridSize, paddingHorizontal: gridSize }}
                asListAction="primary"
                text={i18n.t(`screens.user.create_mission_salary.thirteenth_salary_${regime}`)}
                right={
                    isSelected ? (
                        <View testID={`thirteenth-salary-${regime}-selected`}>
                            <Icon name="check-circle-heavy" size={20} />
                        </View>
                    ) : undefined
                }
                onPress={() => setPay13thSalaryWithSalary(is12x)}
                info={
                    <BodySmallText color={colors.onSurfaceDisabled}>
                        {i18n.t(
                            `screens.user.create_mission_salary.thirteenth_salary_${regime}_description`,
                        )}
                    </BodySmallText>
                }
            />
        );
    };

    return (
        <ModalDialogFrame>
            <ScreenCard
                style={[{ paddingTop: insets.top }]}
                relativeActions={isLarge}
                buttons={[{ title: i18n.t("close"), onPress: onClose, mode: "outlined" }]}
            >
                <VStack h={1}>
                    <Gap gap={0.5} />
                    <BubbleTalk
                        personImageComponent={<DocuIllSvg width="100%" height="100%" />}
                        personSize={44}
                        position="top-center"
                    >
                        <TitleMediumText>
                            {i18n.t(
                                "screens.user.create_mission_salary.thirteenth_salary_editor_title",
                            )}
                        </TitleMediumText>
                    </BubbleTalk>
                    <Gap gap={1.5} />
                    <ButtonField
                        testID="thirteenth-salary-switch"
                        style={{ marginHorizontal: -gridSize, paddingHorizontal: gridSize }}
                        asListAction="primary"
                        valueColor={isMandatory ? colors.onSurfaceDisabled : undefined}
                        disabled={isMandatory}
                        text={i18n.t("screens.user.create_mission_salary.thirteenth_salary_switch")}
                        right={
                            <View pointerEvents="none">
                                <Switch value={has13thSalary} size={44} disabled={isMandatory} />
                            </View>
                        }
                        onPress={isMandatory ? undefined : toggleHas13thSalary}
                        info={
                            isMandatory && (
                                <BodySmallText color={colors.onSurfaceDisabled}>
                                    {i18n.t(
                                        "screens.user.create_mission_salary.thirteenth_salary_mandatory",
                                    )}
                                </BodySmallText>
                            )
                        }
                    />
                    {isMonthly && has13thSalary && (
                        <>
                            <Gap gap={1} />
                            {renderRegime(true)}
                            {renderRegime(false)}
                        </>
                    )}
                </VStack>
            </ScreenCard>
        </ModalDialogFrame>
    );
};

export default ThirteenthSalaryEditorNavScreen;
